import type { JSX } from 'react';
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';

export type ButtonVariant = 'primary' | 'action' | 'ghost' | 'soft' | 'indigo' | 'danger' | 'text';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onClick'> {
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /**
   * Варианты `_ds/components/Button`: primary — оранжевая главная (одна на экран);
   * action — синяя (действие CRM: «Пополнить», «Экспорт»); ghost — контурная;
   * indigo — на промо/тёмных карточках; danger — удаление (красный контур).
   * Свои для SMS-центра: soft — оранжевая подложка; text — без рамки.
   */
  variant?: ButtonVariant;
  /** sm 34 px (7×14, 13 px) · md 40 px (10×18, 14 px) — как `_ds`; lg 48 px — крупные цели на телефоне. */
  size?: ButtonSize;
  icon?: ReactNode;
  /** Иконка справа (стрелка «→», «▾»). */
  iconRight?: ReactNode;
  loading?: boolean;
  /** На всю ширину контейнера. */
  block?: boolean;
  /** Внутренняя ссылка (react-router) — рендерится <Link> с видом кнопки. */
  to?: string;
  /** Внешняя ссылка — <a target=_blank>. */
  href?: string;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Кнопка дизайн-системы fishka / BRASOL (порт `_ds/components/Button` через
 * frontend-wiki): радиус 6, 600, hover — без «прыжков» (только цвет). Для форм
 * antd по-прежнему можно брать antd `Button` (тема уже оранжевая), этот — для
 * шапок, карточек, CTA. Со ссылкой (`to`/`href`) aria-label, title, id и style
 * переносятся на ссылку.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  loading,
  block,
  to,
  href,
  type = 'button',
  className,
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps): JSX.Element {
  const cls = [
    'fk-ds-btn',
    `fk-ds-btn--${variant}`,
    `fk-ds-btn--${size}`,
    block ? 'fk-ds-btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  const inner = (
    <>
      {loading ? <LoadingOutlined /> : icon}
      {children}
      {iconRight}
    </>
  );
  // Ссылке нужны те же подписи для экранного диктора, что и кнопке.
  const linkAttrs = { style: rest.style, title: rest.title, id: rest.id, 'aria-label': rest['aria-label'] };
  if (to && !disabled) {
    return (
      <Link to={to} className={cls} onClick={onClick} {...linkAttrs}>
        {inner}
      </Link>
    );
  }
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls} onClick={onClick} {...linkAttrs}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} onClick={onClick} {...rest}>
      {inner}
    </button>
  );
}
